// -------------- Hook to call the api endpoint for the backtesting -----------------
// ----------------------------------------------------------------------------------
import { useState, useCallback } from 'react';
import { getHoldings } from '../services/api';

// Custom hook to handle holdings API calls
export const useHoldings = () => {

  // create the state variables
  const [holdings, setHoldings] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [chartKey, setChartKey] = useState(null); // tracks which chart was clicked ("test" | "oof-2022" etc.)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // function to fetch holdings by calling the API with the provided parameters
  const fetchHoldings = useCallback(async ({ model, date, split, year = null, kLong = null, kShort = null }) => {
    
    // create a unique key for the chart based on the split and year/Long/Short values
    const key = split === 'oof' 
      ? `oof-${year}-k${kLong ?? 'na'}-${kShort ?? 'na'}`
      : `test-k${kLong ?? 'na'}-${kShort ?? 'na'}`;

    // if the same date and chart key is clicked again, clear the holdings and reset the state
    if (selectedDate === date && chartKey === key) {
      setHoldings(null);
      setSelectedDate(null);
      setChartKey(null);
      return;
    }

    // set the state to be loading and others to null and date to current date and chart key to the new key
    setLoading(true);
    setError(null);
    setChartKey(key);
    setSelectedDate(date);

    try {
      // call the API to get holdings and set the holdings state variable with the response
      const result = await getHoldings({
        model,
        date,
        split,
        year,
        k_long: kLong,
        k_short: kShort,
      });
      setHoldings(result);

    } catch (err) {
      setError(err.message || 'Failed to fetch holdings');
      setHoldings(null);

    } finally {
      setLoading(false);
    }
  }, [selectedDate, chartKey]); // dependencies to ensure the function is updated when selectedDate or chartKey changes

  const clearHoldings = useCallback(() => {
    setHoldings(null);
    setSelectedDate(null);
    setChartKey(null);
    setError(null);
  }, []);

  // return the state variables and functions to be used in the component
  return { holdings, selectedDate, chartKey, loading, error, fetchHoldings, clearHoldings };
};
